import type { Capabilities, MeResponse, Membership, WorkspaceRef } from "@ondo/shared";
import type { CSSProperties, ReactNode } from "react";

import { ORG_TYPE_TAGS } from "../profile/profile.constants.ts";
import { WORKSPACE_LABELS } from "../workspace/workspace.constants.ts";
import { PortalHeader } from "./PortalHeader.tsx";
import { PortalSidebar } from "./PortalSidebar.tsx";
import { usePortalController } from "./usePortalController.ts";
import type { PortalSpec } from "./portal.ts";
import type { PortalView } from "./shell.model.ts";
import "./shell.css";

/**
 * A workspace: a sidebar, a header, and one destination at a time.
 *
 * The accent is set as a custom property on the root rather than baked into the
 * stylesheet, which is what makes this shell role-generic — the file has said so
 * since it was the collector's, and this is the change that made it true. Its
 * two washes are set the same way and for a sharper reason: every tile in the
 * rail used to hard-code the COLLECTOR's tint, so a second portal would have
 * rendered its initials in red. See the role washes in index.css.
 *
 * Rendered by the route for every destination rather than remounted per URL, so
 * moving between destinations updates the body and leaves the sidebar alone.
 *
 * Takes the screen as `children` rather than switching on the destination
 * itself: which component a destination renders is the PORTAL's business — it
 * is the one thing that genuinely differs between two of them — and a switch
 * here would have to know every screen in the product.
 */
export function PortalShell({
  spec,
  destination,
  workspace,
  principal,
  capabilities,
  membership,
  canSwitch,
  onSignOut,
  children,
}: {
  spec: PortalSpec;
  destination: string;
  workspace: WorkspaceRef;
  principal: MeResponse["principal"];
  capabilities: Capabilities;
  /** The caller's membership of this workspace's organisation, or null. */
  membership: Membership | null;
  canSwitch: boolean;
  onSignOut: () => void;
  /** The destination's own screen, given the resolved view. */
  children: (view: PortalView) => ReactNode;
}) {
  const { view } = usePortalController({
    spec,
    destination,
    workspace,
    principal,
    capabilities,
    membership,
    role: workspace.role,
    roleLabel: WORKSPACE_LABELS[workspace.role],
    canSwitch,
    orgKindLabel: (orgType) => ORG_TYPE_TAGS[orgType],
  });

  return (
    <div
      className="shell"
      style={
        {
          "--accent": view.accent,
          "--accent-tint": `var(--role-${workspace.role}-tint)`,
          "--accent-tint-strong": `var(--role-${workspace.role}-tint-strong)`,
        } as CSSProperties
      }
    >
      <PortalSidebar
        eyebrow={view.eyebrow}
        navLabel={view.navLabel}
        nav={view.nav}
        org={view.org}
        user={view.user}
        onSignOut={onSignOut}
      />

      <div className="shell__main">
        <PortalHeader
          title={view.title}
          subtitle={view.subtitle}
          primaryAction={view.primaryAction}
          secondaryAction={view.secondaryAction}
          canSwitch={view.canSwitch}
        />
        <main className="shell__body">{children(view)}</main>
      </div>
    </div>
  );
}
